/**
 * @fileoverview Scratch extension for body pose detection using TensorFlow.js PoseNet model.
 * This extension allows sprites to interact with detected human body poses through webcam input.
 *
 */

const BlockType = require("../../extension-support/block-type");
const ArgumentType = require("../../extension-support/argument-type");
const formatMessage = require("format-message");
const Cast = require("../../util/cast");
const tf = require("@tensorflow/tfjs");
const posenet = require("@tensorflow-models/posenet");

/**
 * Icon svg to be displayed at left edge of each extension block, encoded as a data URI.
 * @type {string}
 */

const blockIconURI =
    "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgZmlsbD0ibm9uZSIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHJ4PSI2IiBmaWxsPSIjMEZCRDhDIi8+PGcgZmlsbD0iI2ZmZiIgc3Ryb2tlPSJub25lIj48Y2lyY2xlIGN4PSIyMCIgY3k9IjEwIiByPSI0Ii8+PHJlY3QgeD0iMTgiIHk9IjE0IiB3aWR0aD0iNCIgaGVpZ2h0PSIxMiIvPjxyZWN0IHg9IjEyIiB5PSIxOCIgd2lkdGg9IjE2IiBoZWlnaHQ9IjQiLz48cmVjdCB4PSIxMiIgeT0iMjIiIHdpZHRoPSI0IiBoZWlnaHQ9IjEwIi8+PHJlY3QgeD0iMjQiIHk9IjIyIiB3aWR0aD0iNCIgaGVpZ2h0PSIxMCIvPjwvZz48L3N2Zz4=";

/**
 * Configuration constants for the pose detection system.
 * @readonly
 */
const CONFIG = {
    // Video frame dimensions for pose detection
    VIDEO_WIDTH: 480,
    VIDEO_HEIGHT: 360,

    // Detection loop timing (in milliseconds)
    DETECTION_INTERVAL: 100,

    // Model loading retry delay (in milliseconds)
    MODEL_RETRY_DELAY: 1000,

    // Minimum confidence score to consider a pose valid
    MIN_POSE_CONFIDENCE: 0.01,

    // Default video settings
    DEFAULT_VIDEO_TRANSPARENCY: 50,

    // Coordinate conversion constants for Scratch stage
    SCRATCH_STAGE_CENTER_X: 240, // Half of 480px stage width
    SCRATCH_STAGE_CENTER_Y: 180, // Half of 360px stage height
};

/**
 * Enum for video state values.
 * @readonly
 * @enum {string}
 */
const VideoState = {
    /** Video is turned off */
    OFF: "off",
    /** Video is on with normal orientation */
    ON: "on",
    /** Video is on with horizontally flipped display */
    ON_FLIPPED: "on-flipped",
};

/**
 * List of supported body parts for pose detection.
 * Maps display names to PoseNet keypoint names.
 * @readonly
 */
const BODY_PARTS = [
    { text: "nose", value: "nose" },
    { text: "left eye", value: "leftEye" },
    { text: "right eye", value: "rightEye" },
    { text: "left ear", value: "leftEar" },
    { text: "right ear", value: "rightEar" },
    { text: "left shoulder", value: "leftShoulder" },
    { text: "right shoulder", value: "rightShoulder" },
    { text: "left elbow", value: "leftElbow" },
    { text: "right elbow", value: "rightElbow" },
    { text: "left wrist", value: "leftWrist" },
    { text: "right wrist", value: "rightWrist" },
    { text: "left hip", value: "leftHip" },
    { text: "right hip", value: "rightHip" },
    { text: "left knee", value: "leftKnee" },
    { text: "right knee", value: "rightKnee" },
    { text: "left ankle", value: "leftAnkle" },
    { text: "right ankle", value: "rightAnkle" },
];

/**
 * Scratch extension class for body pose detection using TensorFlow.js PoseNet.
 * Provides blocks for sprite movement based on detected human body poses.
 */
class Scratch3PoseBodyBlocks {
    /**
     * Create a new PoseBody extension instance.
     * @param {Runtime} runtime - The Scratch runtime instance
     */
    constructor(runtime) {
        /**
         * The Scratch runtime instance
         * @type {Runtime}
         * @private
         */
        this.runtime = runtime;

        /**
         * Current video display state
         * @type {string}
         * @private
         */
        this.globalVideoState = VideoState.ON;

        /**
         * Current video transparency percentage (0-100)
         * @type {number}
         * @private
         */
        this.globalVideoTransparency = CONFIG.DEFAULT_VIDEO_TRANSPARENCY;

        /**
         * Loaded PoseNet model instance
         * @type {object|null}
         * @private
         */
        this.bodyModel = null;

        /**
         * Most recent pose detection result
         * @type {object|null}
         * @private
         */
        this.poseState = null;

        /**
         * Video frame dimensions for pose detection
         * @type {number[]}
         * @private
         */
        this.DIMENSIONS = [CONFIG.VIDEO_WIDTH, CONFIG.VIDEO_HEIGHT];

        /**
         * Flag to track if detection loop is running
         * @type {boolean}
         * @private
         */
        this.isDetectionRunning = false;

        // Initialize TensorFlow.js with WebGL backend for better performance
        this._initializeTensorFlow();

        // Start pose detection if video devices are available
        if (this.runtime.ioDevices) {
            this._startDetectionLoop();
        }

        // Apply default extension settings
        this.projectStarted();
    }

    /**
     * Initialize TensorFlow.js backend asynchronously.
     * Prefers WebGL for better performance, falls back to CPU if needed.
     * @private
     */
    async _initializeTensorFlow() {
        try {
            await tf.ready();

            // Try to use WebGL backend for better performance
            if (tf.getBackend() !== "webgl") {
                await tf.setBackend("webgl");
            }
        } catch (error) {
            console.warn(
                "Failed to initialize WebGL backend, using CPU:",
                error
            );
        }
    }

    /**
     * Get extension metadata including blocks, menus, and display information.
     * @returns {object} Extension metadata object
     */
    getInfo() {
        return {
            id: "posebody",
            name: formatMessage({
                id: "posebody.categoryName",
                default: "Body Sensing",
                description:
                    "Label for the body pose detection extension category",
            }),
            blockIconURI: blockIconURI,
            blocks: [
                {
                    opcode: "goToPart",
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: "posebody.goToPart",
                        default: "go to [PART]",
                        description:
                            "Move sprite to the detected position of a specific body part",
                    }),
                    arguments: {
                        PART: {
                            type: ArgumentType.STRING,
                            menu: "bodyParts",
                            defaultValue: "nose",
                        },
                    },
                },
                {
                    opcode: "videoToggle",
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: "posebody.videoToggle",
                        default: "turn video [VIDEO_STATE]",
                        description:
                            "Control the display of the camera video preview",
                    }),
                    arguments: {
                        VIDEO_STATE: {
                            type: ArgumentType.STRING,
                            menu: "videoMenu",
                            defaultValue: VideoState.ON,
                        },
                    },
                },
                {
                    opcode: "setVideoTransparency",
                    blockType: BlockType.COMMAND,
                    text: formatMessage({
                        id: "posebody.setVideoTransparency",
                        default: "set video transparency to [TRANSPARENCY]",
                        description:
                            "Adjust the opacity of the camera video preview (0-100)",
                    }),
                    arguments: {
                        TRANSPARENCY: {
                            type: ArgumentType.NUMBER,
                            defaultValue: CONFIG.DEFAULT_VIDEO_TRANSPARENCY,
                        },
                    },
                },
            ],
            menus: {
                bodyParts: {
                    acceptReporters: true,
                    items: BODY_PARTS,
                },
                videoMenu: {
                    acceptReporters: true,
                    items: [
                        {
                            text: formatMessage({
                                id: "posebody.videoState.off",
                                default: "off",
                            }),
                            value: VideoState.OFF,
                        },
                        {
                            text: formatMessage({
                                id: "posebody.videoState.on",
                                default: "on",
                            }),
                            value: VideoState.ON,
                        },
                        {
                            text: formatMessage({
                                id: "posebody.videoState.onFlipped",
                                default: "on flipped",
                            }),
                            value: VideoState.ON_FLIPPED,
                        },
                    ],
                },
            },
        };
    }

    /**
     * Check if a valid pose is currently detected.
     * @returns {boolean} True if pose is detected with sufficient confidence
     * @private
     */
    _hasPose() {
        return (
            this.poseState &&
            this.poseState.keypoints &&
            Array.isArray(this.poseState.keypoints) &&
            this.poseState.keypoints.length > 0 &&
            this.poseState.score > CONFIG.MIN_POSE_CONFIDENCE
        );
    }

    /**
     * Load the PoseNet model asynchronously.
     * Includes retry logic for network failures.
     * @returns {Promise<boolean>} True if model loaded successfully
     * @private
     */
    async _loadPoseModel() {
        try {
            // Ensure TensorFlow.js is ready
            await tf.ready();

            // Load PoseNet model with optimized configuration
            this.bodyModel = await posenet.load({
                architecture: "MobileNetV1",
                outputStride: 16,
                inputResolution: {
                    width: CONFIG.VIDEO_WIDTH,
                    height: CONFIG.VIDEO_HEIGHT,
                },
                multiplier: 0.75, // Balance between accuracy and performance
            });
            return true;
        } catch (error) {
            console.error("Failed to load PoseNet model:", error);
            return false;
        }
    }

    /**
     * Start the main pose detection loop.
     * Runs continuously while the extension is active.
     * @private
     */
    _startDetectionLoop() {
        if (this.isDetectionRunning) {
            return; // Prevent multiple loops
        }

        this.isDetectionRunning = true;
        this._detectPoses();
    }

    /**
     * Main pose detection loop that processes video frames.
     * Handles model loading, frame processing, and error recovery.
     * @private
     */
    async _detectPoses() {
        // Load model if not already loaded
        if (!this.bodyModel) {
            const modelLoaded = await this._loadPoseModel();
            if (!modelLoaded) {
                // Retry model loading after delay
                setTimeout(() => this._detectPoses(), CONFIG.MODEL_RETRY_DELAY);
                return;
            }
        }

        try {
            // Get current video frame
            const frame = this.runtime.ioDevices.video.getFrame({
                format: "image-data",
                dimensions: this.DIMENSIONS,
            });

            if (frame && this.bodyModel) {
                // Perform pose detection on the frame
                this.poseState = await this.bodyModel.estimateSinglePose(
                    frame,
                    {
                        flipHorizontal: false,
                        decodingMethod: "single-person",
                    }
                );
            }
        } catch (error) {
            console.error("Error during pose detection:", error);
            // Reset pose state on error to prevent stale data
            this.poseState = null;
        }

        // Schedule next detection cycle
        setTimeout(() => this._detectPoses(), CONFIG.DETECTION_INTERVAL);
    }

    /**
     * Convert PoseNet coordinates to Scratch stage coordinates.
     * PoseNet uses (0,0) at top-left, Scratch uses (0,0) at center.
     * @param {number} x - X coordinate from PoseNet (0 to VIDEO_WIDTH)
     * @param {number} y - Y coordinate from PoseNet (0 to VIDEO_HEIGHT)
     * @param {number} stageWidth - Scratch stage width
     * @param {number} stageHeight - Scratch stage height
     * @returns {object} Converted coordinates {x, y}
     * @private
     */
    _convertToScratchCoordinates(x, y, stageWidth, stageHeight) {
        // Convert from PoseNet space to Scratch space
        const scratchX = (x / this.DIMENSIONS[0]) * stageWidth - stageWidth / 2;
        const scratchY =
            (1 - y / this.DIMENSIONS[1]) * stageHeight - stageHeight / 2;

        return { x: scratchX, y: scratchY };
    }

    /**
     * Find a specific body part in the current pose detection results.
     * @param {string} partName - Name of the body part to find
     * @returns {object|null} Body part keypoint or null if not found
     * @private
     */
    _findBodyPart(partName) {
        if (!this._hasPose()) {
            return null;
        }

        return this.poseState.keypoints.find(
            (keypoint) =>
                keypoint.part === partName &&
                keypoint.score > CONFIG.MIN_POSE_CONFIDENCE
        );
    }

    /**
     * Initialize extension with default settings when project starts.
     * Called automatically by Scratch runtime.
     */
    projectStarted() {
        this.setVideoTransparency({
            TRANSPARENCY: this.globalVideoTransparency,
        });
        this.videoToggle({ VIDEO_STATE: this.globalVideoState });
    }

    /**
     * Move sprite to the detected position of a specific body part.
     * Block implementation for "go to [PART]".
     * @param {object} args - Block arguments
     * @param {string} args.PART - Name of body part to move to
     * @param {object} util - Block utility object with target sprite reference
     */
    goToPart(args, util) {
        const partName = Cast.toString(args.PART);
        const bodyPart = this._findBodyPart(partName);

        if (!bodyPart) {
            // Silently fail if part not detected - this is normal during pose detection
            return;
        }

        // Get current stage dimensions
        const stageWidth = this.runtime.constructor.STAGE_WIDTH;
        const stageHeight = this.runtime.constructor.STAGE_HEIGHT;

        // Convert pose coordinates to Scratch coordinate system
        const scratchCoords = this._convertToScratchCoordinates(
            bodyPart.position.x,
            bodyPart.position.y,
            stageWidth,
            stageHeight
        );

        // Ensure sprite stays within stage boundaries
        const boundedPosition = util.target.keepInFence(
            scratchCoords.x,
            scratchCoords.y
        );

        // Move sprite to the calculated position
        util.target.setXY(boundedPosition[0], boundedPosition[1]);
    }

    /**
     * Control video preview display state.
     * Block implementation for "turn video [VIDEO_STATE]".
     * @param {object} args - Block arguments
     * @param {string} args.VIDEO_STATE - Desired video state (off/on/on-flipped)
     */
    videoToggle(args) {
        const requestedState = Cast.toString(args.VIDEO_STATE);
        this.globalVideoState = requestedState;

        if (requestedState === VideoState.OFF) {
            // Turn off video preview
            this.runtime.ioDevices.video.disableVideo();
        } else {
            // Request camera access and enable video
            this._enableVideoPreview(requestedState === VideoState.ON_FLIPPED);
        }
    }

    /**
     * Enable video preview with camera access request.
     * @param {boolean} shouldMirror - Whether to mirror the video horizontally
     * @private
     */
    async _enableVideoPreview(shouldMirror = false) {
        try {
            // Request camera permissions
            await navigator.mediaDevices.getUserMedia({
                video: {
                    width: CONFIG.VIDEO_WIDTH,
                    height: CONFIG.VIDEO_HEIGHT,
                },
            });

            // Enable video preview in Scratch
            this.runtime.ioDevices.video.enableVideo();
            this.runtime.ioDevices.video.mirror = shouldMirror;
        } catch (error) {
            // Reset video state on failure
            this.globalVideoState = VideoState.OFF;
        }
    }

    /**
     * Set video preview transparency.
     * Block implementation for "set video transparency to [TRANSPARENCY]".
     * @param {object} args - Block arguments
     * @param {number} args.TRANSPARENCY - Transparency percentage (0-100)
     */
    setVideoTransparency(args) {
        const transparency = Cast.toNumber(args.TRANSPARENCY);

        // Clamp transparency value to valid range
        const clampedTransparency = Math.max(0, Math.min(100, transparency));

        this.globalVideoTransparency = clampedTransparency;
        this.runtime.ioDevices.video.setPreviewGhost(clampedTransparency);
    }
}

module.exports = Scratch3PoseBodyBlocks;
